/**
 * ReactivateMemberModal — confirm + run the reinstatement of an inactive (departed /
 * deactivated) member, entirely from the UI (#575).
 *
 * The API re-enables the member's identity-provider (Entra) sign-in BEFORE it flips the
 * membership active. If the identity provider refuses, the API answers 502 with
 * `code: SIGN_IN_REENABLE_FAILED` and leaves the member Inactive — so the app never shows
 * an "Active" member who can't sign in. This dialog then stays open, says plainly that the
 * member was NOT reactivated and why, and offers "Retry sign-in re-enable".
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Member } from '../../services/api/userService';
import memberService, {
  isSignInReenableFailure,
  ReactivateMemberResult,
} from '../../services/api/memberService';
import { extractApiError } from '../../utils/apiError';

interface ReactivateMemberModalProps {
  member: Member;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful reactivation (the dialog closes itself). */
  onReactivated?: (member: Member, result: ReactivateMemberResult) => void;
  /** Raise the overlay when opened above another high-z overlay. */
  zIndex?: number;
}

const BodyText = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 1px;
  }
`;

const ErrorContent = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ErrorTitle = styled.strong`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

export const ReactivateMemberModal = ({
  member,
  isOpen,
  onClose,
  onReactivated,
  zIndex,
}: ReactivateMemberModalProps) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<{ message: string; signInFailure: boolean } | null>(null);
  const name = `${member.firstName} ${member.lastName}`;

  const mutation = useMutation({
    mutationFn: () => memberService.reactivateMember(member.id),
    onSuccess: (result) => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['members'] });
      onReactivated?.(member, result);
      onClose();
    },
    onError: (err) => {
      setError({
        message: extractApiError(err, 'Failed to reactivate member. Please try again.'),
        signInFailure: isSignInReenableFailure(err),
      });
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Reactivate ${name}`}
      size="sm"
      zIndex={zIndex}
      footer={
        <Footer>
          <Button variant="secondary" onClick={handleClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            isLoading={mutation.isPending}
            disabled={mutation.isPending}
          >
            {error?.signInFailure ? 'Retry sign-in re-enable' : 'Reactivate'}
          </Button>
        </Footer>
      }
    >
      {error && (
        <ErrorBanner role="alert">
          <AlertTriangle />
          <ErrorContent>
            {error.signInFailure && <ErrorTitle>Sign-in could not be re-enabled</ErrorTitle>}
            {error.message}
          </ErrorContent>
        </ErrorBanner>
      )}
      <BodyText>
        Reactivating <strong>{name}</strong> ({member.email}) restores their access to this
        organization and re-enables their sign-in, so they can sign in again with their existing
        credentials.
      </BodyText>
    </Modal>
  );
};

export default ReactivateMemberModal;
